import {
  type License,
  type SpecialQualifications,
  WorkerRequirementDomain,
} from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { qualificationSubstitutions } from "@src/appV2/Qualifications/utils";
import { forwardRef, Fragment } from "react";

import { type WorkerRequirement } from "../../Documents/api/types";
import { isCredentialRequirementForQualification } from "../../Documents/isCredentialRequirementForQualification";
import { LicenseCard } from "../../Licenses/LicenseCard";
import { LicenseCardSkeleton } from "../../Licenses/Skeleton";
import { LICENSE_SORT_ORDER } from "../constants";
import { CredentialRequirementContent } from "./CredentialRequirementContent";
import { RoleDetailsEmptyState } from "./EmptyState";
import { NoLicenseRequired } from "./NoLicenseRequired";

interface RoleDetailsPageContentBaseProps {
  qualificationLabel: string;
  licenses: License[];
  nlcStates: Set<string>;
  isLoading: boolean;
  onNlcStatesClick: () => void;
  onAddLicense: () => void;
  onRemoveLicense: (licenseId: string) => Promise<void>;
  onRenewLicense: (license: License) => Promise<void>;
  isAutoApproved: boolean;
  workerCredentialRequirements: WorkerRequirement[];
}

function RoleDetailsPageContentBase(
  props: RoleDetailsPageContentBaseProps,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    qualificationLabel,
    workerCredentialRequirements,
    licenses,
    nlcStates,
    onNlcStatesClick,
    onAddLicense,
    onRemoveLicense,
    onRenewLicense,
    isLoading,
    isAutoApproved,
  } = props;

  if (isLoading) {
    return (
      <Stack ref={ref} sx={{ padding: 5, flex: 1, overflow: "auto" }} spacing={8}>
        {Array.from({ length: 5 }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <LicenseCardSkeleton key={`skeleton-${index}`} />
        ))}
      </Stack>
    );
  }

  const hiringBaseCredentialRequirement = workerCredentialRequirements.find((requirement) =>
    isCredentialRequirementForQualification({
      requirement,
      domains: [WorkerRequirementDomain.HIRING],
      qualificationName: qualificationLabel,
      isBaseCredential: true,
    })
  );

  const flexBaseCredentialRequirement = workerCredentialRequirements.find((requirement) =>
    isCredentialRequirementForQualification({
      requirement,
      domains: [WorkerRequirementDomain.FLEX],
      qualificationName: qualificationLabel,
      isBaseCredential: true,
    })
  );

  const baseCredentialToUse = hiringBaseCredentialRequirement ?? flexBaseCredentialRequirement;

  if (isAutoApproved) {
    return isDefined(baseCredentialToUse) ? (
      <CredentialRequirementContent
        qualificationLabel={qualificationLabel}
        workerBaseCredentialRequirement={baseCredentialToUse}
      />
    ) : (
      <NoLicenseRequired />
    );
  }

  const sortLicensesByStatus = (licenseA: License, licenseB: License) => {
    const statusA = licenseA.attributes.status;
    const statusB = licenseB.attributes.status;

    const orderA = LICENSE_SORT_ORDER[statusA] ?? Number.MAX_SAFE_INTEGER;
    const orderB = LICENSE_SORT_ORDER[statusB] ?? Number.MAX_SAFE_INTEGER;

    return orderA - orderB;
  };

  const getQualificationWithSubstitutionsText = () => {
    const substitutions =
      qualificationSubstitutions[qualificationLabel.toUpperCase() as SpecialQualifications];

    if (!substitutions || substitutions.length === 0) {
      return <b>{qualificationLabel}</b>;
    }

    if (substitutions.length === 1) {
      return (
        <>
          <b>{qualificationLabel}</b> or <b>{substitutions[0]}</b>
        </>
      );
    }

    return (
      <>
        {[qualificationLabel, ...substitutions.slice(0, -1)].map((qualification, index) => (
          <Fragment key={qualification}>
            <b>{qualification}</b>
            {index < substitutions.length - 1 && ","}{" "}
          </Fragment>
        ))}
        or <b>{substitutions.at(-1)}</b>
      </>
    );
  };

  return (
    <Stack ref={ref} sx={{ padding: 5, flex: 1, gap: 4, overflow: "auto" }}>
      <Text
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          paddingBottom: 5,
        })}
      >
        To book {qualificationLabel} shifts, you need to upload a valid{" "}
        {getQualificationWithSubstitutionsText()} license
      </Text>

      {licenses
        // Sort licenses by status priority
        .sort(sortLicensesByStatus)
        // don't show archived licenses
        .filter((license) => license.attributes.status !== LicenseStatuses.ARCHIVED)
        .map((license) => (
          <LicenseCard
            key={license.id}
            state={license.attributes.state}
            nlcStates={nlcStates}
            multiState={license.attributes.multiState ?? false}
            status={license.attributes.status}
            expiresAt={license.attributes.expiresAt ?? undefined}
            number={license.attributes.number}
            licenseQualification={license.attributes.qualification}
            onRemove={async () => {
              await onRemoveLicense(license.id);
            }}
            onRenew={async () => {
              await onRenewLicense(license);
            }}
            onNlcStatesClick={onNlcStatesClick}
          />
        ))}

      {licenses.every((license) => license.attributes.status === LicenseStatuses.ARCHIVED) && (
        <RoleDetailsEmptyState onAddLicense={onAddLicense} />
      )}
    </Stack>
  );
}

export const RoleDetailsPageContent = forwardRef(RoleDetailsPageContentBase);
